import type { CategoryChildLimit, CategoryKind, CategoryOverviewRow, ParentCategory } from '@/types';
import type { SpaceBudget } from '@/types/spaces';
import { UNCATEGORIZED_CATEGORY_ID, computeCategoryBudgetStatus } from '@/components/orcamento/helpers';
import { buildCategoryTree, type CategoryTree } from '@/lib/categories/tree';
import { UNCATEGORIZED_LABEL } from './budget';
import { budgetProgress, type BudgetProgress } from './home';

// The space's categories, read from the budget (`GET /spaces/{id}/budget`, cents) over the shared taxonomy: every root
// with the space's spending in it and its limit, the personal overview's row model (reais), plus the limits set on a
// subcategory. The home's Categorias card and the budget sheet show them (`spaceCategorySpending`). Nothing here is
// fetched: both inputs are reads the caller already has.

export interface SpaceCategoryOverview { rows: CategoryOverviewRow[]; childLimits: CategoryChildLimit[] }

// A space only holds real spending, so its directory lists what the API accepts a budget line on — the same rule as
// core.api's PluggyCategoryBusinessRules (IsIncomeCategory / IsBudgetExcludedCategory): income is the "01" root, and
// same-person transfers, investments and credit-card payments are never spending. Everything else is, loans, taxes,
// bank fees, donations and transfers to others included (a display colour group is not a budget rule).
const INCOME_CODE = '01';
const NEVER_SPENDING_ROOTS = new Set(['Same person transfer', 'Investments']);
const CREDIT_CARD_PAYMENT = 'Credit card payment';

export function spaceCategoryKind(root: Pick<ParentCategory, 'id' | 'description'>): CategoryKind {
  if (root.id.startsWith(INCOME_CODE) || root.description === 'Income') return 'income';
  if (NEVER_SPENDING_ROOTS.has(root.description)) return 'neutral';
  return 'expense';
}

// The taxonomy a space budget can use: the spending roots, without the credit-card-payment branch (Pluggy nests it
// under Transfers). The budget editor offers only these.
export function spaceBudgetTaxonomy(tree: readonly ParentCategory[] | undefined): ParentCategory[] {
  return (tree ?? []).filter((parent) => spaceCategoryKind(parent) === 'expense').map((parent) => {
    const excluded = new Set(parent.children.filter((child) => child.description === CREDIT_CARD_PAYMENT).map((child) => child.id));
    // Descendants of an excluded node go with it (the subtree is flattened, each node naming its parent).
    let grew = excluded.size > 0;
    while (grew) {
      grew = false;
      for (const child of parent.children) {
        if (!excluded.has(child.id) && child.parentId && excluded.has(child.parentId)) { excluded.add(child.id); grew = true; }
      }
    }
    return excluded.size ? { ...parent, children: parent.children.filter((child) => !excluded.has(child.id)) } : parent;
  });
}

export function spaceCategoryOverview(budget: SpaceBudget, tree: ParentCategory[] | undefined): SpaceCategoryOverview {
  const parents = tree ?? [];
  // `ParentCategory.children` is the whole subtree flattened (grandchildren carry their `parentId`); the tree gives
  // every node its depth, its root and its immediate parent.
  const categoryTree = buildCategoryTree(parents);
  const isDescendant = (id: string) => (categoryTree.byId.get(id)?.depth ?? 0) >= 2;
  const lineOf = new Map(budget.lines.map((line) => [line.categoryId, line]));
  const unbudgetedOf = new Map(budget.unbudgeted.map((row) => [row.categoryId, row.spentCents]));

  // Every root the taxonomy lists, then the roots the budget names that the taxonomy no longer does, then the
  // expenses without a shared category. A child line lives under its root as a child limit, never as a row.
  const roots: { id: string; name: string; icon: string | null; children: ParentCategory['children'] }[] = spaceBudgetTaxonomy(parents)
    .map((parent) => ({ id: parent.id, name: parent.descriptionTranslated, icon: parent.icon, children: parent.children }));
  const known = new Set(roots.map((root) => root.id));
  const orphanIds = [...new Set([...budget.lines.map((line) => line.categoryId), ...budget.unbudgeted.flatMap((row) => row.categoryId ? [row.categoryId] : [])])]
    .filter((id) => !known.has(id) && !isDescendant(id));
  for (const id of orphanIds) roots.push({ id, name: `Categoria ${id}`, icon: null, children: [] });

  const rows = roots.map((root): CategoryOverviewRow => {
    const line = lineOf.get(root.id);
    // A root line counts everything beneath it; otherwise the root's spending is its children's lines plus what no
    // line covers, which the API already groups by root.
    const spentCents = line ? line.spentCents
      : outermostLines(root.id, root.children, lineOf, categoryTree).reduce((sum, child) => sum + child.spentCents, 0) + (unbudgetedOf.get(root.id) ?? 0);
    return row(root.id, root.name, root.icon, spentCents, line?.limitCents ?? null);
  });
  const uncategorized = unbudgetedOf.get(null);
  if (uncategorized) rows.push(row(UNCATEGORIZED_CATEGORY_ID, UNCATEGORIZED_LABEL, null, uncategorized, null));

  const childLimits = budget.lines.flatMap((line): CategoryChildLimit[] => {
    const node = categoryTree.byId.get(line.categoryId);
    if (!node || node.depth < 2) return [];
    const limit = line.limitCents / 100;
    const spent = line.spentCents / 100;
    // The tree already falls a leaf's icon back to its IMMEDIATE parent's, then the root's.
    return [{ categoryRef: node.id, rootRef: node.rootId, name: node.label, icon: node.iconName, spent, txCount: 0,
      limit, burnPercent: burnPercent(spent, limit), status: computeCategoryBudgetStatus(spent, limit) }];
  });
  return { rows, childLimits };
}

// A line counts everything beneath it, so a root without a line of its own sums only the OUTERMOST lines under it: a
// grandchild's line is already inside its parent's line when both exist, and adding it again would count it twice.
function outermostLines(rootId: string, descendants: ParentCategory['children'], lineOf: ReadonlyMap<string, SpaceBudget['lines'][number]>, categoryTree: CategoryTree): SpaceBudget['lines'] {
  const underAnotherLine = (id: string): boolean => {
    let parentId = categoryTree.byId.get(id)?.parentId ?? null;
    while (parentId && parentId !== rootId) {
      if (lineOf.has(parentId)) return true;
      parentId = categoryTree.byId.get(parentId)?.parentId ?? null;
    }
    return false;
  };
  return descendants.flatMap((child) => {
    const line = lineOf.get(child.id);
    return line && !underAnotherLine(child.id) ? [line] : [];
  });
}

function row(categoryRef: string, name: string, icon: string | null, spentCents: number, limitCents: number | null): CategoryOverviewRow {
  const spent = spentCents / 100;
  const limit = limitCents === null ? null : limitCents / 100;
  return { categoryRef, source: 'pluggy', name, icon, color: null, kind: 'expense', spent, average3mo: 0, limit,
    burnPercent: burnPercent(spent, limit), status: computeCategoryBudgetStatus(spent, limit), txCount: 0, needsReviewCount: 0, isArchived: false };
}

// One decimal, like the personal overview (CategoryOverviewService), so the two pages read alike.
function burnPercent(spent: number, limit: number | null): number | null {
  if (limit === null || limit <= 0) return null;
  return Math.round((spent / limit) * 1000) / 10;
}

export interface SpaceCategorySpend {
  categoryId: string;
  label: string;
  spentCents: number;
  limitCents: number | null;
  /** Against the limit; null without one (nothing to measure against). */
  progress: BudgetProgress | null;
  /** The limits set on a subcategory of this root; their spending is already inside the root's. */
  children: SpaceCategorySpend[];
}

// What the space spent, by category: every root that took spending or carries a limit, its own or one set on a
// subcategory, most spent first (a limit nobody spent against yet still shows, at the end), and under each root the
// limits set on its subcategories. Roots the space never touched stay out: a reading of the period, not a directory.
export function spaceCategorySpending(budget: SpaceBudget, tree: ParentCategory[] | undefined): SpaceCategorySpend[] {
  const { rows, childLimits } = spaceCategoryOverview(budget, tree);
  const cents = (reais: number): number => Math.round(reais * 100);
  const spend = (categoryId: string, label: string, spent: number, limit: number | null, children: SpaceCategorySpend[] = []): SpaceCategorySpend => {
    const limitCents = limit === null ? null : cents(limit);
    return { categoryId, label, spentCents: cents(spent), limitCents, progress: budgetProgress(cents(spent), limitCents), children };
  };
  const byLabel = (a: SpaceCategorySpend, b: SpaceCategorySpend): number => a.label.localeCompare(b.label, 'pt-BR') || a.categoryId.localeCompare(b.categoryId);
  const childrenOf = (rootRef: string): SpaceCategorySpend[] => childLimits
    .filter((child) => child.rootRef === rootRef)
    .map((child) => spend(child.categoryRef, child.name, child.spent, child.limit))
    .sort((a, b) => b.spentCents - a.spentCents || byLabel(a, b));
  return rows
    .map((row) => spend(row.categoryRef, row.name, row.spent, row.limit, childrenOf(row.categoryRef)))
    .filter((row) => row.spentCents > 0 || row.limitCents !== null || row.children.length > 0)
    .sort((a, b) => b.spentCents - a.spentCents || byLabel(a, b));
}
